import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useStoryStore } from '../stores/storyStore';
import NodeEditor from '../components/NodeEditor';
import CustomNode from '../components/CustomNode';

const { FiSave, FiPlay, FiPlus, FiArrowLeft, FiCheck, FiAlertTriangle } = FiIcons;

const nodeTypes = {
  custom: CustomNode,
};

const StoryEditor = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { stories, updateStory, updateStoryNodes } = useStoryStore();
  const [story, setStory] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodeEditor, setShowNodeEditor] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Load story data
  useEffect(() => {
    const foundStory = stories.find(s => s.id === storyId);
    if (foundStory) {
      setStory(foundStory);
      setNodes(foundStory.nodes || []);
      setEdges(foundStory.edges || []);
    } else {
      navigate('/dashboard');
    }
  }, [storyId, stories, navigate]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setShowNodeEditor(true);
  }, []);

  const addNewNode = useCallback(() => {
    const newNode = {
      id: `node_${Date.now()}`,
      type: 'custom',
      position: { x: Math.random() * 400 + 100, y: Math.random() * 400 + 100 },
      data: {
        label: 'Neue Frage',
        type: 'multiple-choice',
        questionText: '',
        choices: [
          { id: 1, text: '', isCorrect: false, nextNodeId: null },
          { id: 2, text: '', isCorrect: false, nextNodeId: null }
        ],
        timer: null,
        imageUrl: '',
        videoUrl: '',
        // Short answer fields
        correctAnswers: [''],
        ignoreCase: false,
        correctNextNodeId: null,
        incorrectNextNodeId: null
      }
    };

    setNodes((nds) => [...nds, newNode]);
  }, [setNodes]);

  const updateNode = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, ...newData } }
          : node
      )
    );
  }, [setNodes]);

  const deleteNode = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    setShowNodeEditor(false);
    setSelectedNode(null);
  }, [setNodes, setEdges]);

  const validateStory = useCallback(() => {
    const errors = [];
    
    // Check if there's a start node
    const startNode = nodes.find(node => node.id === 'start' || node.data?.type === 'start');
    if (!startNode) {
      errors.push('Geschichte muss einen Startknoten haben');
    }

    // Check if there's at least one end node
    const endNodes = nodes.filter(node => node.data?.type === 'end');
    if (endNodes.length === 0) {
      errors.push('Geschichte muss mindestens einen Endknoten haben');
    }

    // Check each question node for completeness
    nodes.forEach(node => {
      if (node.data?.type === 'multiple-choice') {
        if (!node.data.questionText?.trim()) {
          errors.push(`Frage "${node.data.label}" fehlt Fragetext`);
        }
        
        const validChoices = node.data.choices?.filter(choice => choice.text?.trim());
        if (!validChoices || validChoices.length < 2) {
          errors.push(`Frage "${node.data.label}" braucht mindestens 2 Antwortmöglichkeiten`);
        }

        const hasCorrectAnswer = node.data.choices?.some(choice => choice.isCorrect);
        if (!hasCorrectAnswer) {
          errors.push(`Frage "${node.data.label}" muss eine richtige Antwort haben`);
        }
      }

      if (node.data?.type === 'true-false') {
        if (!node.data.questionText?.trim()) {
          errors.push(`Frage "${node.data.label}" fehlt Fragetext`);
        }
      }

      if (node.data?.type === 'short-answer') {
        if (!node.data.questionText?.trim()) {
          errors.push(`Frage "${node.data.label}" fehlt Fragetext`);
        }

        const validAnswers = node.data.correctAnswers?.filter(answer => answer?.trim());
        if (!validAnswers || validAnswers.length === 0) {
          errors.push(`Frage "${node.data.label}" braucht mindestens eine richtige Antwort`);
        }

        if (!node.data.correctNextNodeId) {
          errors.push(`Frage "${node.data.label}" fehlt Pfad für richtige Antwort`);
        }

        if (!node.data.incorrectNextNodeId) {
          errors.push(`Frage "${node.data.label}" fehlt Pfad für falsche Antwort`);
        }
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  }, [nodes]);

  const saveStory = useCallback(() => {
    updateStoryNodes(storyId, nodes, edges);
    alert('Geschichte erfolgreich gespeichert!');
  }, [storyId, nodes, edges, updateStoryNodes]);

  const publishStory = useCallback(() => {
    if (validateStory()) {
      updateStory(storyId, { status: 'published' });
      updateStoryNodes(storyId, nodes, edges);
      alert('Geschichte erfolgreich veröffentlicht!');
    } else {
      alert('Bitte beheben Sie die Validierungsfehler vor der Veröffentlichung');
    }
  }, [storyId, nodes, edges, updateStory, updateStoryNodes, validateStory]);

  if (!story) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#2b6777' }}></div>
          <p className="mt-4 font-rubik" style={{ color: '#2b6777' }}>Geschichte wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 transition-colors"
              style={{ color: '#2b6777' }}
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold font-rubik" style={{ color: '#2b6777' }}>{story.title}</h1>
              <p className="text-sm font-rubik" style={{ color: '#2b6777' }}>Geschichteneditor</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {validationErrors.length > 0 && (
              <div className="flex items-center space-x-1 text-orange-600">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" />
                <span className="text-sm font-rubik">{validationErrors.length} Probleme</span>
              </div>
            )}
            
            <button
              onClick={saveStory}
              className="flex items-center space-x-2 px-4 py-2 transition-colors font-rubik"
              style={{ color: '#2b6777' }}
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              <span>Speichern</span>
            </button>

            <button
              onClick={publishStory}
              className={`flex items-center space-x-2 px-4 py-2 rounded transition-colors font-rubik ${
                story.status === 'published'
                  ? 'bg-green-100 text-green-700'
                  : 'text-white'
              }`}
              style={story.status === 'draft' ? { backgroundColor: '#c8d8e4' } : {}}
            >
              <SafeIcon icon={story.status === 'published' ? FiCheck : FiPlay} className="w-4 h-4" />
              <span>{story.status === 'published' ? 'Veröffentlicht' : 'Veröffentlichen'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Flow Editor */}
      <div className="flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
        >
          <Background />
          <Controls />
          <MiniMap />
          
          <Panel position="top-right">
            <div className="flex flex-col space-y-2">
              <motion.button
                onClick={addNewNode}
                className="flex items-center space-x-2 px-4 py-2 rounded shadow transition-colors text-white font-rubik"
                style={{ backgroundColor: '#c8d8e4' }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Frage hinzufügen</span>
              </motion.button>

              {validationErrors.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded p-3 max-w-xs">
                  <div className="flex items-center space-x-2 mb-2">
                    <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-800 font-rubik">Validierungsprobleme</span>
                  </div>
                  <ul className="text-xs text-orange-700 space-y-1 font-rubik">
                    {validationErrors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Panel>
        </ReactFlow>
      </div>

      {/* Node Editor Modal */}
      {showNodeEditor && selectedNode && (
        <NodeEditor
          node={selectedNode}
          onClose={() => {
            setShowNodeEditor(false);
            setSelectedNode(null);
          }}
          onUpdate={(newData) => updateNode(selectedNode.id, newData)}
          onDelete={() => deleteNode(selectedNode.id)}
          allNodes={nodes}
        />
      )}
    </div>
  );
};

export default StoryEditor;